import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, createVNode as _createVNode, createTextVNode as _createTextVNode } from "vue"
import _imports_0 from '../../../static/icons/info.svg'


const _hoisted_1 = { class: "quantity-selector-row" }
const _hoisted_2 = { class: "quantity-selector-row__container" }
const _hoisted_3 = { class: "quantity-selector-row__image-container" }
const _hoisted_4 = ["src"]
const _hoisted_5 = { class: "quantity-selector-row__details" }
const _hoisted_6 = { class: "quantity-selector-row__text" }
const _hoisted_7 = {
  key: 0,
  class: "quantity-selector-row__name"
}
const _hoisted_8 = {
  key: 1,
  class: "quantity-selector-row__discount"
}
const _hoisted_9 = {
  key: 2,
  class: "quantity-selector-row__divider"
}
const _hoisted_10 = {
  key: 3,
  class: "quantity-selector-row__article-number"
}
const _hoisted_11 = ["disabled", "max"]
const _hoisted_12 = {
  key: 0,
  class: "quantity-selector-row__warning"
}

import { ref, computed, inject, watch, onMounted, nextTick } from 'vue';
import { configuratorStateKey } from '../ProductConfiguratorKeys';
import { injectionKeys as litiumKeys } from '@motillo/drapejs-litium';
import { inputFocusKey } from '../ProductConfiguratorKeys';
import useContext from '@/composables/useContext';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import QuantitySelectorRowDropdown from './QuantitySelectorRowDropdown.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'QuantitySelectorRow',
  props: {
  quantity: {
    type: Number,
    required: true
  },
  variant: {
    type: Object,
    required: true
  },
  hasDecoration: {
    type: Boolean,
    default: false
  },
  variantSupported: {
    type: Boolean,
    default: false
  },
  selectedDecorationName: {
    type: String,
    default: ''
  },
  isGiftCard: {
    type: Boolean,
    default: false
  }
},
  emits: ['updateQuantity'],
  setup(__props, { emit: __emit }) {

const { page, formatPrice } = useContext();
const { configuratorState } = inject(configuratorStateKey);
const { websiteText } = useWebsiteTexts();
const emit = __emit;
const toLitiumMediaUrl = inject(litiumKeys.toLitiumMediaUrlKey, () => '');
const { updateFocusState } = inject(inputFocusKey);

const props = __props;

const inputVal = ref(null);
const oldInputVal = ref(null);
const inputNumber = computed(() => {
  if (typeof inputVal.value === 'string') return 0;
  if (
    !Number.isNaN(inputVal.value) &&
    !Number.isNaN(parseInt(inputVal.value, 10))
  ) {
    return Math.min(
      maxQuantityInput.value,
      Math.abs(parseInt(inputVal.value, 10))
    );
  }
  return 0;
});
const shownArticleNumber = computed(() => {
  if (typeof props.variant?.fields?.NAVArticleNumber === 'string'
    && props.variant.fields.NAVArticleNumber.trim() !== ''
  ) {
    return props.variant.fields.NAVArticleNumber;
  }

  return props.variant?.articleNumber;
});

const variantImage = computed(() => {
  if (page.value.dataJson?.datajson?.product?.isGiftcard) {
    const designAddon =
      configuratorState.value.selections?.selectedAddons?.find(
        (a) => a.id == 'Addon-Design'
      );
    const giftCardImage = props.variant?.images?.find((i) =>
      i.filename.includes(designAddon?.variant?.id)
    );
    if (!!giftCardImage) {
      return toLitiumMediaUrl(giftCardImage.id, { maxWidth: 42 });
    }
    const defaultImage = props.variant?.images?.find((i) =>
      i.filename.includes('default')
    );
    if (!!defaultImage) {
      return toLitiumMediaUrl(defaultImage.id, { maxWidth: 42 });
    }
    return '';
  } else {
    const productImage = props.variant.images[0];
    if (!!productImage) {
      return toLitiumMediaUrl(productImage.id, { maxWidth: 42 });
    }
    return '';
  }
});

const variantName = computed(() => {
  return props.variant.fields?.Color?.[0]?.name || '';
});

const variantDiscount = computed(() => {
  const { campaignPrice } = props.variant;
  if (!campaignPrice) return 0;
  return `(-${formatPrice(
    Math.abs(
      (campaignPrice / (props.variant.price || props.variant.originalPrice)) *
        -100
    ),
    undefined,
    '',
    '',
    'decimal'
  )}%)`;
});

const stockData = computed(() => {
  return props.hasDecoration
    ? props.variant.printableStock
    : props.variant.stock;
});

const mappedStockData = computed(() => {
  const stock = [];
  const variantStock = stockData.value;

  if (
    variantStock.currentStock.amount === 0 &&
    variantStock.incomingStock.length === 0
  ) {
    stock.push({
      text: `${websiteText('productdetailspage_outofstock').value}`,
      icon: 'out',
      id: variantStock.currentStock.systemId,
      quantity: 0,
      totalQuantity: 0,
      type: 'option'
    });
  }

  if (variantStock.currentStock.amount !== 0) {
    stock.push({
      text: `${websiteText('global__in_stock').value}: ${
        variantStock.currentStock.amount
      }`,
      icon: 'available',
      id: variantStock.currentStock.systemId,
      quantity: variantStock.currentStock.amount,
      totalQuantity: variantStock.currentStock.amount,
      type: 'option'
    });
  }

  if (variantStock.incomingStock.length > 0) {
    stock.push({
      text: websiteText('productdetailspage_totalstock').value,
      id: 1,
      quantity: 0,
      type: 'headline'
    });

    variantStock.incomingStock.forEach((s) => {
      const date = new Date(s.date);
      const totalQuantity =
        variantStock.currentStock.amount +
        s.amount +
        variantStock.incomingStock.reduce((acc, cur) => {
          const incomingDate = new Date(cur.date);
          return incomingDate < date ? acc + cur.amount : acc;
        }, 0);

      stock.push({
        text: `${date.getDate()}/${date.getMonth() + 1}: ${totalQuantity}`,
        icon: 'incoming',
        id: s.systemId,
        quantity: s.amount,
        totalQuantity,
        type: 'option'
      });
    });
  }

  return stock;
});

const maxQuantityInput = computed(() => {
  return mappedStockData.value.reduce((acc, curr) => acc + curr.quantity, 0);
});

const outOfStock = computed(
  () => maxQuantityInput.value === 0 || !props.variantSupported
);

function updateFocus(event: FocusEvent) {
  if (event.type === 'focus') {
    updateFocusState(true);
  }
  if (event.type === 'blur') {
    updateFocusState(false);
  }
}

function updateQuantity() {
  inputVal.value = inputNumber.value === 0 ? '' : inputNumber.value;
  emit('updateQuantity', {
    quantity: inputNumber.value,
    id: props.variant.systemId
  });
}

onMounted(() => {
  nextTick(() => {
    inputVal.value = !inputVal.value && props.quantity ? props.quantity : null;
  });
});

watch(outOfStock, (val) => {
  if (val) {
    updateQuantity();
  }
});

const isVariantSupported = computed(() => props.variantSupported);
watch(isVariantSupported, (val) => {
  if (!val) {
    oldInputVal.value = inputVal.value;
    inputVal.value = null;
    updateQuantity();
  } else if (oldInputVal.value) {
    inputVal.value = oldInputVal.value;
    updateQuantity();
  }
});

watch(configuratorState.value, (val) => {
  if (!val.sumQuantity) {
    inputVal.value = null;
  }
});

const notAvailableText = computed(() => {
  if (isVariantSupported.value) {
    return '';
  }

  if (props.selectedDecorationName === '') {
    return '';
  }

  return websiteText('decoration__not_available', {
    decoration: props.selectedDecorationName,
    variant: variantName.value
  }).value;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[1] || (_cache[1] = _createElementVNode("div", { class: "quantity-selector-row__spacer" }, null, -1)),
        _cache[2] || (_cache[2] = _createElementVNode("div", { class: "quantity-selector-row__overlay" }, null, -1)),
        _createElementVNode("img", {
          class: "quantity-selector-row__image",
          src: variantImage.value,
          alt: "Variant image"
        }, null, 8, _hoisted_4)
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("div", _hoisted_6, [
          (variantName.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(variantName.value), 1))
            : _createCommentVNode("", true),
          (variantDiscount.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(variantDiscount.value), 1))
            : _createCommentVNode("", true),
          (shownArticleNumber.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9))
            : _createCommentVNode("", true),
          (shownArticleNumber.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(shownArticleNumber.value), 1))
            : _createCommentVNode("", true)
        ]),
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((inputVal).value = $event)),
          onKeyup: updateQuantity,
          class: "quantity-selector-row__input",
          type: "number",
          min: "0",
          disabled: outOfStock.value,
          max: maxQuantityInput.value,
          onFocus: updateFocus,
          onBlur: updateFocus
        }, null, 40, _hoisted_11), [
          [_vModelText, inputVal.value]
        ]),
        _createVNode(QuantitySelectorRowDropdown, {
          class: "quantity-selector-row__dropdown",
          quantity: inputNumber.value,
          "stock-data": mappedStockData.value,
          isGiftCard: __props.isGiftCard
        }, null, 8, ["quantity", "stock-data", "isGiftCard"])
      ])
    ]),
    (notAvailableText.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
          _cache[3] || (_cache[3] = _createElementVNode("img", {
            class: "quantity-selector-row__warning_icon",
            src: _imports_0
          }, null, -1)),
          _createTextVNode(" " + _toDisplayString(notAvailableText.value), 1)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})